import type { FC } from "react";
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { getPaths } from "core/getPaths";
import { Table } from "shared/Components";
import type { TableColumnType } from "shared/Components/Table/Components/SimpleTable";
import { ExpandableColumnDataKey } from "shared/Components/Table/Components/SimpleTable";
import { useLeague } from "shared/hooks";
import type { PaginationTokenType, PaymentRequestModel } from "shared/types";
import { PaymentRequestAudienceTypes } from "shared/types";
import { TableColumnLoaderType } from "shared/types/Table";
import type { MenuDotsOption } from "shared/uikit";
import { Button, MenuDots, Text } from "shared/uikit";
import { ButtonVariantsEnum, TextStylesEnum, TextVariantsEnum } from "shared/uikit/types";

import AmountStats from "./AmountStats";
import CollectedAmountCell from "./CollectedAmountCell";
import PaymentRequestProgressCell from "./PaymentRequestProgressCell";
import { getPaymentRequestAmountSummaries } from "./amountSummary";

const TransactionDetailsDialog = lazy(() => import("shared/Components/TransactionDetailsDialog"));
const ChooseTeamModal = lazy(() => import("shared/Components/PaymentModal/ChooseTeamModal"));

const { league: leagueRoutes } = getPaths();

const pageSize = 10;

interface PaymentRequestsTableProps {
	leagueId: string;
	allowCreatePayments?: boolean;
	asPage?: boolean;
}

const PaymentRequestsTable: FC<PaymentRequestsTableProps> = ({ leagueId, asPage, allowCreatePayments }) => {
	const { t } = useTranslation();

	const { getPaymentRequests, getData: getLeagueData } = useLeague();
	const { loadingPaymentRequests, paymentRequests, nextListingPaymentRequestsToken } = getLeagueData();

	const [paymentRequestList, setPaymentRequestList] = useState<PaymentRequestModel[]>(paymentRequests);

	const [transactionDetailsOpen, setTransactionDetailsOpen] = useState<string | null>(null);
	const [seeTeamsModal, setSeeTeamsModal] = useState<string | null>(null);

	const fetchData = useCallback(
		async (leagueId: string, afterToken?: PaginationTokenType) => {
			const data = await getPaymentRequests({ leagueId, after: afterToken });
			setPaymentRequestList(c => (!!afterToken ? [...c, ...data.paymentRequests] : data.paymentRequests));
		},
		[getPaymentRequests]
	);

	useEffect(() => {
		fetchData(leagueId, null);
	}, [fetchData, leagueId]);

	const handleLoadMore = useCallback(() => {
		nextListingPaymentRequestsToken && fetchData(nextListingPaymentRequestsToken);
	}, [nextListingPaymentRequestsToken, fetchData]);

	const getOptions = useCallback(
		(rowData: PaymentRequestModel): MenuDotsOption[] => {
			const { totalAmount, collectedAmount, relatedPaymentRequests } = rowData;
			const options: MenuDotsOption[] = [];

			const isAnyPayments = relatedPaymentRequests?.some(x => !!x.collectedAmount);

			if (isAnyPayments) {
				options.push({
					name: "See payments",
					onClick: () => setTransactionDetailsOpen(rowData.id)
				});
			}

			if (totalAmount !== collectedAmount) {
				options.push({
					name: "See pending payments",
					onClick: () => setSeeTeamsModal(rowData.id)
				});
			}

			return options;
		},
		[setTransactionDetailsOpen]
	);

	const tableColumns: TableColumnType[] = useMemo(
		() => [
			{
				label: t("product"),
				minWidth: 260,
				Cell: ({
					rowData: { name, linkedObject, audiences, relatedPaymentRequests }
				}: {
					rowData: PaymentRequestModel;
				}) => {
					let invoiceTo = "-";
					if (audiences?.length) {
						const firstAudience = audiences[0]!;
						switch (firstAudience.type) {
							case PaymentRequestAudienceTypes.SOCCER_CLUB_GENERIC:
								invoiceTo = t("club");
						}
					}

					if (relatedPaymentRequests?.length) {
						const firstAudience = relatedPaymentRequests[0].audiences?.length
							? relatedPaymentRequests[0].audiences[0]!
							: null;
						if (firstAudience?.type === PaymentRequestAudienceTypes.SOCCER_TEAM_PLAYER) {
							invoiceTo = t("players");
						}
						if (firstAudience?.type === PaymentRequestAudienceTypes.SOCCER_CLUB_GENERIC) {
							invoiceTo = t("clubs");
						}
					}

					return (
						<div>
							<Text>{name}</Text>
							{linkedObject?.competition?.name && <Text className="text-hint">{linkedObject.competition.name}</Text>}
							{invoiceTo && (
								<Text className="text-hint">
									{t("payment:invoice_to")}: <b>{invoiceTo}</b>
								</Text>
							)}
						</div>
					);
				},
				dataKey: "product"
			},
			// {
			// 	label: t("payment:invoice_to"),
			// 	width: 110,
			// 	Cell: ({ rowData: { audiences, relatedPaymentRequests } }: { rowData: PaymentRequestModel }) => {
			// 		if (audiences?.length) {
			// 			const firstAudience = audiences[0]!;
			// 			switch (firstAudience.type) {
			// 				case PaymentRequestAudienceTypes.SOCCER_CLUB_GENERIC:
			// 					return t("club");
			// 			}
			// 		}
			//
			// 		if (relatedPaymentRequests?.length) {
			// 			const firstAudience = relatedPaymentRequests[0].audiences?.length
			// 				? relatedPaymentRequests[0].audiences[0]!
			// 				: null;
			// 			if (firstAudience?.type === PaymentRequestAudienceTypes.SOCCER_TEAM_PLAYER) {
			// 				return t("players");
			// 			}
			// 			if (firstAudience?.type === PaymentRequestAudienceTypes.SOCCER_CLUB_GENERIC) {
			// 				return t("clubs");
			// 			}
			// 		}
			//
			// 		return "-";
			// 	},
			// 	dataKey: "invoiceTo"
			// },
			{
				label: t("invited"),
				width: 90,
				Cell: ({ rowData: { audiences, relatedPaymentRequests } }: { rowData: PaymentRequestModel }) => {
					if (audiences?.length) {
						const firstAudience = audiences[0]!;
						if (firstAudience.type === PaymentRequestAudienceTypes.SOCCER_CLUB_GENERIC) {
							return (
								<div>
									<Text variants={TextVariantsEnum.BodyMedium}>{firstAudience.club!.teams.length || 0}</Text>
									<Text variants={TextVariantsEnum.BodyMedium} className="mt-2 text-hint">
										{t("teams")}
									</Text>
								</div>
							);
						}
					}

					if (relatedPaymentRequests?.length) {
						const firstAudience = relatedPaymentRequests[0].audiences?.length
							? relatedPaymentRequests[0].audiences[0]!
							: null;
						if (firstAudience?.type === PaymentRequestAudienceTypes.SOCCER_TEAM_PLAYER) {
							const playersCount = relatedPaymentRequests.reduce((acc, pr) => acc + (pr?.audiences?.length || 0), 0);

							return (
								<div>
									<Text variants={TextVariantsEnum.BodyMedium}>{playersCount}</Text>
									<Text variants={TextVariantsEnum.BodyMedium} className="mt-2 text-hint">
										{t("players")}
									</Text>
								</div>
							);
						}

						if (firstAudience?.type === PaymentRequestAudienceTypes.SOCCER_CLUB_GENERIC) {
							const teamsCount = relatedPaymentRequests.reduce(
								(acc, pr) =>
									acc + (pr.audiences?.reduce((accAud, prAud) => accAud + (prAud?.club?.teams?.length || 0), 0) || 0),
								0
							);

							return (
								<div>
									<Text variants={TextVariantsEnum.BodyMedium}>{teamsCount}</Text>
									<Text variants={TextVariantsEnum.BodyMedium} className="mt-2 text-hint">
										{t("teams")}
									</Text>
								</div>
							);
						}
					}

					return "-";
				},
				dataKey: "invited"
			},
			{
				label: t("collected"),
				width: 230,
				Cell: ({ rowData }: { rowData: PaymentRequestModel }) => {
					const info = getPaymentRequestAmountSummaries(rowData);
					const defaultCurrencySummary = info.find(x => x.currency === rowData?.currency);
					return <CollectedAmountCell summaries={defaultCurrencySummary ? [defaultCurrencySummary] : info} />;
				},
				dataKey: "collected"
			},
			{
				label: t("progress"),
				minWidth: 220,
				Cell: ({ rowData }: { rowData: PaymentRequestModel }) => {
					const info = getPaymentRequestAmountSummaries(rowData);
					const defaultCurrencySummary = info.find(x => x.currency === rowData?.currency);
					return <PaymentRequestProgressCell summaries={defaultCurrencySummary ? [defaultCurrencySummary] : info} />;
				},
				dataKey: "progress"
			},
			{
				label: "",
				width: 80,
				dataKey: ExpandableColumnDataKey,
				expandCell: ({ rowData, isExpanded }: { rowData: PaymentRequestModel; isExpanded: boolean }) => (
					<>
						{isExpanded && (
							<Suspense>
								<AmountStats summaries={getPaymentRequestAmountSummaries(rowData)} />
							</Suspense>
						)}
					</>
				)
			},
			{
				label: "",
				width: 60,
				Cell: ({ rowData }: { rowData: PaymentRequestModel }) => <MenuDots options={getOptions(rowData)} />,
				dataKey: "options",
				loaderType: TableColumnLoaderType.menuDots
			}
		],
		[getOptions, t]
	);

	// const filterBlock = useMemo(
	// 	() => (
	// 		<Button size={ButtonSizesEnum.SM} onClick={() => setCreatePaymentDialog(true)}>
	// 			Create payment
	// 		</Button>
	// 	),
	// 	[setCreatePaymentDialog]
	// );

	if (!paymentRequestList.length && !loadingPaymentRequests && !allowCreatePayments) {
		return null;
	}

	return (
		<>
			<Table
				// filterBlock={filterBlock}
				columns={tableColumns}
				data={paymentRequestList}
				loading={loadingPaymentRequests}
				loadingMore={loadingPaymentRequests && !!nextListingPaymentRequestsToken}
				label={
					<div className="flex items-center gap-2">
						<Text style={TextStylesEnum.Default} variants={TextVariantsEnum.H6} className="whitespace-nowrap">
							{t("payment:payment_requests")}
						</Text>
						{!asPage && (
							<Button
								link={`${leagueRoutes.getPath()}/${leagueId}/finance/requests/all`}
								variant={ButtonVariantsEnum.LINK}
							>
								{t("explore_all")}
							</Button>
						)}
					</div>
				}
				tableSpacingClasses="px-4"
				rowHeight={90}
				onLoadMore={asPage ? handleLoadMore : undefined}
				pageSize={pageSize}
				expandable
			/>
			{seeTeamsModal && (
				<Suspense>
					<ChooseTeamModal infoView reqId={seeTeamsModal} onClose={() => setSeeTeamsModal(null)} />
				</Suspense>
			)}
			{transactionDetailsOpen && (
				<Suspense>
					<TransactionDetailsDialog
						reqId={transactionDetailsOpen}
						handleClose={() => setTransactionDetailsOpen(null)}
					/>
				</Suspense>
			)}
		</>
	);
};

export default PaymentRequestsTable;
